import { Vec3 } from '../math/vec3';
import { AABB } from '../math/aabb';
import { Ray } from '../math/ray';
import { Heightfield } from '../geo/heightfield';

export interface BoxCollider { kind: 'box'; aabb: AABB; tag?: string; enabled: boolean }
export interface CylinderCollider { kind: 'cyl'; x: number; z: number; r: number; yMin: number; yMax: number; tag?: string; enabled: boolean }
export type StaticCollider = BoxCollider | CylinderCollider;

/** A walkable surface patch (plank, bridge, floor) the capsule can stand on. Walls are colliders; floors are these. */
export interface FloorPatch { aabb: AABB; enabled: boolean; tag?: string }

export interface GroundHit { y: number; normal: Vec3; floor: FloorPatch | null }

const CELL = 8;

/**
 * Static collision world: non-overlapping heightfield regions + boxes/cylinders (walls, trunks)
 * + floor patches, with a uniform-grid broadphase for the colliders.
 */
export class StaticWorld {
  readonly heightfields: Heightfield[] = [];
  readonly colliders: StaticCollider[] = [];
  readonly floors: FloorPatch[] = [];
  private grid = new Map<string, StaticCollider[]>();
  private floorGrid = new Map<string, FloorPatch[]>();
  /** Height returned where no heightfield covers a point (e.g. the void). */
  fallbackHeight = -50;

  private key(cx: number, cz: number): string { return cx + ',' + cz; }

  addHeightfield(h: Heightfield): void { this.heightfields.push(h); }

  addBox(aabb: AABB, tag?: string): BoxCollider {
    const c: BoxCollider = { kind: 'box', aabb, tag, enabled: true };
    this.colliders.push(c);
    this.index(c, aabb);
    return c;
  }
  addCylinder(x: number, z: number, r: number, yMin: number, yMax: number, tag?: string): CylinderCollider {
    const c: CylinderCollider = { kind: 'cyl', x, z, r, yMin, yMax, tag, enabled: true };
    this.colliders.push(c);
    this.index(c, AABB.fromMinMax(x - r, yMin, z - r, x + r, yMax, z + r));
    return c;
  }
  addFloor(aabb: AABB, tag?: string): FloorPatch {
    const f: FloorPatch = { aabb, enabled: true, tag };
    this.floors.push(f);
    this.forCells(aabb, (k) => { let a = this.floorGrid.get(k); if (!a) this.floorGrid.set(k, (a = [])); a.push(f); });
    return f;
  }
  private index(c: StaticCollider, aabb: AABB): void {
    this.forCells(aabb, (k) => { let a = this.grid.get(k); if (!a) this.grid.set(k, (a = [])); a.push(c); });
  }
  private forCells(aabb: AABB, fn: (k: string) => void): void {
    const x0 = Math.floor(aabb.min.x / CELL), x1 = Math.floor(aabb.max.x / CELL);
    const z0 = Math.floor(aabb.min.z / CELL), z1 = Math.floor(aabb.max.z / CELL);
    for (let cx = x0; cx <= x1; cx++) for (let cz = z0; cz <= z1; cz++) fn(this.key(cx, cz));
  }

  /** Colliders whose cells overlap the query box. May contain duplicates. */
  query(aabb: AABB, out: StaticCollider[] = []): StaticCollider[] {
    out.length = 0;
    this.forCells(aabb, (k) => { const a = this.grid.get(k); if (a) for (const c of a) if (c.enabled && out.indexOf(c) < 0) out.push(c); });
    return out;
  }
  queryFloors(aabb: AABB, out: FloorPatch[] = []): FloorPatch[] {
    out.length = 0;
    this.forCells(aabb, (k) => { const a = this.floorGrid.get(k); if (a) for (const f of a) if (f.enabled && out.indexOf(f) < 0) out.push(f); });
    return out;
  }

  heightfieldAt(x: number, z: number): Heightfield | null {
    for (const h of this.heightfields) if (h.contains(x, z)) return h;
    return null;
  }

  /** Terrain height only (no floors). */
  terrainHeight(x: number, z: number): number {
    const h = this.heightfieldAt(x, z);
    return h ? h.height(x, z) : this.fallbackHeight;
  }

  /**
   * Ground under a point: the highest support (terrain, floor patch, or box top) that is
   * at or below y + stepUp. Returns the support height and normal.
   */
  ground(x: number, y: number, z: number, stepUp = 0.4, out: GroundHit = { y: 0, normal: new Vec3(0, 1, 0), floor: null }): GroundHit {
    const hf = this.heightfieldAt(x, z);
    let best = hf ? hf.height(x, z) : this.fallbackHeight;
    out.floor = null;
    if (hf) hf.normal(x, z, out.normal); else out.normal.set(0, 1, 0);
    const limit = y + stepUp;
    // floor patches (planks, bridges) may be climbed onto from a bit lower: wading up onto a plank
    const floorLimit = y + stepUp + 1.2;
    _q.min.set(x - 0.01, y - 5, z - 0.01); _q.max.set(x + 0.01, floorLimit, z + 0.01);
    for (const f of this.queryFloors(_q, _floors)) {
      const a = f.aabb;
      if (x >= a.min.x && x <= a.max.x && z >= a.min.z && z <= a.max.z && a.max.y <= floorLimit && a.max.y > best) {
        best = a.max.y; out.normal.set(0, 1, 0); out.floor = f;
      }
    }
    for (const c of this.query(_q, _cols)) {
      if (c.kind !== 'box') continue;
      const a = c.aabb;
      if (x >= a.min.x && x <= a.max.x && z >= a.min.z && z <= a.max.z && a.max.y <= limit && a.max.y > best) {
        best = a.max.y; out.normal.set(0, 1, 0); out.floor = null;
      }
    }
    out.y = best;
    return out;
  }

  /**
   * Push a vertical capsule (center-bottom at pos, radius r, height h) out of walls in XZ.
   * Boxes whose top is within stepUp of the feet are treated as steps, not walls.
   * Returns true if any correction was applied. Optionally records the push normal.
   */
  resolveCapsule(pos: Vec3, r: number, h: number, stepUp = 0.4, pushOut?: Vec3): boolean {
    let moved = false;
    if (pushOut) pushOut.set(0, 0, 0);
    _q.min.set(pos.x - r, pos.y, pos.z - r); _q.max.set(pos.x + r, pos.y + h, pos.z + r);
    const feet = pos.y + stepUp, head = pos.y + h;
    for (const c of this.query(_q, _cols)) {
      if (c.kind === 'cyl') {
        if (c.yMax <= feet || c.yMin >= head) continue;
        const dx = pos.x - c.x, dz = pos.z - c.z;
        const d = Math.hypot(dx, dz), minD = c.r + r;
        if (d < minD) {
          const nx = d > 1e-5 ? dx / d : 1, nz = d > 1e-5 ? dz / d : 0;
          const push = minD - d;
          pos.x += nx * push; pos.z += nz * push;
          if (pushOut) { pushOut.x += nx; pushOut.z += nz; }
          moved = true;
        }
      } else {
        const a = c.aabb;
        if (a.max.y <= feet || a.min.y >= head) continue;
        // expanded box in XZ
        const ex0 = a.min.x - r, ex1 = a.max.x + r, ez0 = a.min.z - r, ez1 = a.max.z + r;
        if (pos.x <= ex0 || pos.x >= ex1 || pos.z <= ez0 || pos.z >= ez1) continue;
        // corner rounding: if outside the core box on both axes, treat as cylinder around the corner
        const cx = Math.max(a.min.x, Math.min(a.max.x, pos.x)), cz = Math.max(a.min.z, Math.min(a.max.z, pos.z));
        const dx = pos.x - cx, dz = pos.z - cz;
        const inside = dx === 0 && dz === 0;
        if (!inside) {
          const d = Math.hypot(dx, dz);
          if (d >= r) continue;
          const nx = dx / d, nz = dz / d, push = r - d;
          pos.x += nx * push; pos.z += nz * push;
          if (pushOut) { pushOut.x += nx; pushOut.z += nz; }
          moved = true;
          continue;
        }
        const dl = pos.x - ex0, dr = ex1 - pos.x, db = pos.z - ez0, df = ez1 - pos.z;
        const m = Math.min(dl, dr, db, df);
        if (m === dl) { pos.x = ex0; if (pushOut) pushOut.x -= 1; }
        else if (m === dr) { pos.x = ex1; if (pushOut) pushOut.x += 1; }
        else if (m === db) { pos.z = ez0; if (pushOut) pushOut.z -= 1; }
        else { pos.z = ez1; if (pushOut) pushOut.z += 1; }
        moved = true;
      }
    }
    if (pushOut && moved) pushOut.normalize();
    return moved;
  }

  /** Is a sphere overlapping any wall collider? */
  sphereBlocked(p: Vec3, r: number): boolean {
    _q.min.set(p.x - r, p.y - r, p.z - r); _q.max.set(p.x + r, p.y + r, p.z + r);
    for (const c of this.query(_q, _cols)) {
      if (c.kind === 'cyl') {
        if (p.y + r < c.yMin || p.y - r > c.yMax) continue;
        const d = Math.hypot(p.x - c.x, p.z - c.z);
        if (d < c.r + r) return true;
      } else if (c.aabb.intersectsSphere(p, r)) return true;
    }
    return false;
  }

  /**
   * Sphere-cast from `from` toward `to`: returns the fraction [0,1] at which a sphere of radius r
   * first touches terrain or a wall. Marches in small steps (camera booms are short).
   */
  sphereCast(from: Vec3, to: Vec3, r: number): number {
    const dist = from.distanceTo(to);
    if (dist < 1e-4) return 1;
    const steps = Math.max(4, Math.ceil(dist / 0.2));
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      _p.copy(from).lerp(to, t);
      const groundY = this.terrainHeight(_p.x, _p.z);
      if (_p.y - r < groundY) return Math.max(0, (i - 1) / steps);
      if (this.sphereBlocked(_p, r)) return Math.max(0, (i - 1) / steps);
    }
    return 1;
  }

  /** Ray vs walls + terrain. Returns hit distance or -1. Used for line of sight. */
  raycast(origin: Vec3, dir: Vec3, maxDist: number): number {
    _ray.origin.copy(origin); _ray.dir.copy(dir);
    let best = -1;
    _q.min.copy(origin).min(_p.copy(origin).addScaled(dir, maxDist));
    _q.max.copy(origin).max(_p);
    for (const c of this.query(_q, _cols)) {
      let t = -1;
      if (c.kind === 'cyl') t = _ray.intersectVerticalCylinder(c.x, c.z, c.r, c.yMin, c.yMax);
      else t = _ray.intersectAABB(c.aabb);
      if (t >= 0 && t <= maxDist && (best < 0 || t < best)) best = t;
    }
    // terrain: coarse march
    const steps = Math.ceil(maxDist / 1.0);
    for (let i = 1; i <= steps; i++) {
      const t = Math.min(maxDist, i);
      if (best >= 0 && t > best) break;
      _p.copy(origin).addScaled(dir, t);
      if (_p.y < this.terrainHeight(_p.x, _p.z)) { best = t; break; }
    }
    return best;
  }

  clear(): void {
    this.heightfields.length = 0; this.colliders.length = 0; this.floors.length = 0;
    this.grid.clear(); this.floorGrid.clear();
  }
}

const _q = new AABB(new Vec3(), new Vec3());
const _cols: StaticCollider[] = [];
const _floors: FloorPatch[] = [];
const _p = new Vec3();
const _ray = new Ray();
